import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { YachtLogo } from '../components/YachtLogo';
import { Book, Code, Store, MessageSquare, ArrowRight, Github, Mail } from 'lucide-react';

import Navbar from '../components/Navbar';

export default function Resources() {
  const resources = [
    {
      title: "Documentation",
      description: "Deep dive into the Yacht Labs architecture, engine logic, and deployment strategies.",
      icon: Book,
      link: "/docs"
    },
    {
      title: "Community Blog",
      description: "Insights on the future of product development, AI synthesis, and founder leverage.",
      icon: MessageSquare,
      link: "/blog"
    },
    {
      title: "Build Marketplace",
      description: "Explore and clone high-fidelity build modules synthesized by the community.",
      icon: Store,
      link: "/marketplace"
    }
  ];

  return (
    <div className="min-h-screen bg-[#050505] text-white font-sans selection:bg-zinc-100 selection:text-black flex flex-col">
      <Navbar />

      <main className="flex-1">
        <section className="pt-48 pb-24 px-8">
          <div className="max-w-6xl mx-auto text-center space-y-8">
            <motion.h1 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-6xl md:text-8xl font-mono uppercase tracking-tighter mb-8"
            >
              The <span className="text-[#0074D9]">Commons.</span>
            </motion.h1>
            <p className="text-zinc-500 text-xl font-light max-w-2xl mx-auto leading-relaxed">
              Knowledge and tools to help you master the art of product synthesis. From advanced documentation to community-driven modules.
            </p>
          </div>
        </section>

        <section className="py-24 px-8">
          <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
            {resources.map((resource, i) => (
              <Link 
                key={i}
                to={resource.link}
                className="group p-12 border border-zinc-900 bg-zinc-950/50 rounded-[40px] hover:border-[#0074D9]/30 transition-all"
              >
                <div className="w-16 h-16 bg-zinc-900 border border-zinc-800 rounded-2xl flex items-center justify-center text-zinc-500 group-hover:text-[#0074D9] group-hover:bg-[#0074D9]/10 transition-all mb-12">
                   <resource.icon size={32} />
                </div>
                <h3 className="text-3xl font-mono uppercase tracking-tight mb-6">{resource.title}</h3>
                <p className="text-zinc-500 font-light leading-relaxed mb-8">{resource.description}</p>
                <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-[0.2em] text-[#0074D9] group-hover:gap-5 transition-all">
                  Access Resource <ArrowRight size={14} />
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="py-32 px-8 bg-zinc-950 border-t border-white/5">
           <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center gap-16">
              <div className="flex-1 space-y-8">
                <h2 className="tech-label text-[#0074D9]">The Laboratory</h2>
                <h3 className="text-4xl font-mono uppercase tracking-tighter leading-tight">Join the conversation on the future of code.</h3>
                <p className="text-zinc-400 text-lg font-light leading-relaxed">
                   We're building an ecosystem for ambitious builders. Follow our progress on GitHub, or reach out to the team directly for early access to experimental features.
                </p>
                <div className="flex gap-4">
                   <a href="#" className="px-6 py-3 border border-zinc-800 rounded-xl text-xs font-mono uppercase tracking-widest hover:border-white transition-all flex items-center gap-2">
                     <Github size={14} /> GitHub
                   </a>
                   <a href="mailto:hello@yacht-labs.com" className="px-6 py-3 border border-zinc-800 rounded-xl text-xs font-mono uppercase tracking-widest hover:border-white transition-all flex items-center gap-2">
                     <Mail size={14} /> Contact
                   </a>
                </div>
              </div>
              <div className="flex-1 h-64 bg-black border border-zinc-900 rounded-[32px] p-8 flex items-center justify-center">
                 <YachtLogo className="w-24 h-24 opacity-10" />
              </div>
           </div>
        </section>
      </main>

      <footer className="p-24 border-t border-zinc-900 bg-black">
        <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-4 gap-24">
          <div className="col-span-2 space-y-8">
            <Link to="/" className="flex items-center gap-4">
              <YachtLogo className="w-8 h-8" />
              <span className="font-mono text-2xl uppercase tracking-widest">Yacht Labs</span>
            </Link>
            <p className="text-zinc-500 font-light leading-relaxed max-w-sm">
              Synthesizing the future of digital architecture. Empowering founders to bridge the gap between intent and existence.
            </p>
          </div>
          <div className="space-y-6">
            <h4 className="tech-label text-white">Platform</h4>
            <div className="flex flex-col gap-4">
              <Link to="/product" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Synthesis</Link>
              <Link to="/use-cases" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Use Cases</Link>
              <Link to="/solutions" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Solutions</Link>
              <Link to="/pricing" className="text-xs font-mono text-zinc-500 hover:text-white uppercase tracking-widest">Pricing</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
